import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/useLocale';
import { dateLocales } from '@/i18n/config';
import { localizedPath } from '@/i18n/routes';

interface LegalDocumentProps {
  namespace: 'privacy' | 'cookiePolicy' | 'accessibility';
}

function localizeHtmlLinks(html: string, locale: 'it' | 'en'): string {
  if (locale === 'it') return html;
  return html.replace(/href="\/([^"#][^"]*)"/g, (_match, path: string) => {
    if (path.startsWith('en/')) return _match;
    const localized = localizedPath(`/${path}`, 'en');
    return `href="${localized}" class="text-primary hover:underline"`;
  });
}

export const LegalDocument = ({ namespace }: LegalDocumentProps) => {
  const { t } = useTranslation(namespace);
  const { locale } = useLocale();

  const formattedDate = new Date().toLocaleDateString(dateLocales[locale], {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const body = useMemo(() => {
    const raw = (t('body') as string).replace(/^>\s*/, '').trim();
    return localizeHtmlLinks(raw, locale);
  }, [t, locale]);

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-4 text-4xl font-semibold text-foreground md:text-5xl">
          {t('pageTitle')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('lastUpdated')} {formattedDate}
        </p>
      </div>

      <div
        className="prose prose-slate max-w-none dark:prose-invert legal-content [&_a]:text-primary [&_a]:hover:underline"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </>
  );
};
